import type { LineChartDataPoint, BarChartDataPoint } from "@shared/components/charts"

// Types
export interface OverviewSection {
  id: string
  title: string
  value: string
  subtitle?: string
  chartData?: LineChartDataPoint[] | BarChartDataPoint[]
  chartType: 'line' | 'bar' | 'none'
  visible: boolean
  tooltip?: string
}

// Mock Data - Today Section
export const TODAY_DATA = {
  grossVolume: "€0.00 EUR",
  time: "16:25",
  yesterday: {
    label: "Yesterday",
    value: "€0.00 EUR"
  },
  balance: {
    label: "BALANCE",
    value: "EUR €0.00",
    subtitle: "Estimated future payouts",
    link: "View"
  },
  payouts: {
    label: "PAYOUTS",
    value: "—",
    link: "View"
  }
}

// Mock Data - Overview Sections
export const OVERVIEW_SECTIONS: OverviewSection[] = [
  // Payments Category
  {
    id: 'gross-volume',
    title: 'Gross volume',
    value: 'EUR €0.00',
    subtitle: 'EUR €0.00 (previous period)',
    chartType: 'line',
    visible: true,
    tooltip: 'Estimated volume from payments that are settled to your Stripe balance.\nExcludes payment record data.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'net-volume',
    title: 'Net volume',
    value: 'EUR €0.00',
    subtitle: 'EUR €0.00 (previous period)',
    chartType: 'line',
    visible: true,
    tooltip: 'Estimated volume from payments that are settled to your Stripe balance after fees, refunds, disputes, and Connect transfers have been deducted.\nExcludes payment record data.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'net-volume-from-sales',
    title: 'Net volume from sales',
    value: 'EUR €0.00',
    subtitle: 'EUR €0.00 previous period',
    chartType: 'none',
    visible: true,
    tooltip: 'Net volume from sales transactions after deducting fees, refunds, and disputes.',
    chartData: [],
  },
  {
    id: 'payments',
    title: 'Payments',
    value: '0',
    subtitle: '0 previous period',
    chartType: 'none',
    visible: true,
    tooltip: 'Total number of payment attempts including successful, failed, and pending payments.',
    chartData: [],
  },
  {
    id: 'successful-payments',
    title: 'Successful payments',
    value: '0',
    subtitle: '0 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'Number of payments that were successfully processed and completed.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'refunded-payments',
    title: 'Refunded payments',
    value: 'EUR €0.00',
    subtitle: 'EUR €0.00 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'Total amount refunded to customers during the selected period.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'high-risk-payments',
    title: 'High-risk payments',
    value: '0',
    subtitle: '0 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'Vinr Radar uses machine learning to determine if a payment is likely to be fraudulent.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'dispute-activity',
    title: 'Dispute activity',
    value: 'EUR €0.00',
    subtitle: 'EUR €0.00 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'Estimated number of disputes divided by the number of successful payments in the same time series. Inquiries and Retrievals are not included in your dispute rate.\nLearn more',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'dispute-count',
    title: 'Dispute count',
    value: '0',
    subtitle: '0 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'Estimated number of disputes. Inquiries and Retrievals are not included',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'dispute-payments',
    title: 'Dispute payments',
    value: 'EUR €0.00',
    subtitle: 'EUR €0.00 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'Total amount of payments that have been disputed by customers.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'failed-payments',
    title: 'Failed payments',
    value: '0',
    subtitle: '0 previous period',
    chartType: 'none',
    visible: true,
    tooltip: 'Most recent payment failures excluding blocked payments.',
    chartData: [],
  },
  // Customers Category
  {
    id: 'customers',
    title: 'Customers',
    value: '0',
    subtitle: '0 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'Estimated gross volume is the total spend per customer, including guest customers. The numbers do not include refunds and disputes.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'new-customers',
    title: 'New customers',
    value: '0',
    subtitle: '0 previous period',
    chartType: 'none',
    visible: true,
    tooltip: 'Number of new customers created, including customers that are subsequently deleted. This number does not include guest customers.',
    chartData: [],
  },
  {
    id: 'top-customers-by-spend',
    title: 'Top customers by spend',
    value: '',
    subtitle: '',
    chartType: 'none',
    visible: true,
    tooltip: 'List of customers ranked by their total spending during the selected period.',
    chartData: [],
  },
  {
    id: 'spend-per-customer',
    title: 'Spend per customer',
    value: 'EUR €0.00',
    subtitle: 'EUR €0.00 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'Estimated revenue for all payments created with a customer, divided by the total number of customers with payments.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  // Subscriptions Category
  {
    id: 'active-subscribers',
    title: 'Active subscribers',
    value: '12532',
    subtitle: '12532 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'Total number of subscribers with active, non-canceled subscriptions.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'new-subscribers',
    title: 'New subscribers',
    value: '12532',
    subtitle: '12532 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'This is the total number of new non-trial, paid subscribers (not including free plans). It does not include existing subscribers who add additional subscriptions.\nLearn more',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'new-trials',
    title: 'New trials',
    value: '12532',
    subtitle: '12532 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'This is the total number of new subscriptions that started with a free trial period.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'churned-revenue',
    title: 'Churned Revenue',
    value: 'EUR €0.00',
    subtitle: 'EUR €0.00 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'This is the total MRR lost during the period due to downgrades or churn.\nLearn more',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'subscriber-churn-rate',
    title: 'Subscriber churn rate',
    value: '0%',
    subtitle: '0% (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'The churn rate is measured by the sum of churned subscribers in the past 30 days divided by the number of active subscribers as of 30 days ago, plus any new subscribers in those 30 days.\nLearn more',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'mrr',
    title: 'MRR',
    value: 'EUR €0.00',
    subtitle: 'EUR €0.00 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'Monthly recurring revenue (MRR) is your normalized monthly revenue from all active and past-due subscriptions. It\'s the best single measure of the health and trajectory of a recurring revenue business.\nLearn more',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'average-revenue-per-user',
    title: 'Average revenue per user',
    value: 'EUR €0.00',
    subtitle: 'EUR €0.00 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'This is your average MRR per user. It\'s calculated by dividing your MRR by the total number of active subscribers.\nLearn more',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'subscriber-lifetime-value',
    title: 'Subscriber lifetime value',
    value: 'EUR €0.00',
    subtitle: 'EUR €0.00 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'This is an estimate of the total revenue you can expect to collect from your average subscriber before they churn.\nLearn more',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'trial-conversion-rate',
    title: 'Trial conversion rate',
    value: '0%',
    subtitle: '0% (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'The percentage of trials that converted to active subscriptions during a rolling 30-day period. This may exceed 100% if customers have recently converted and their trial ended before the current 30-day period.\nLearn more',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  // Invoices Category
  {
    id: 'invoices',
    title: 'Invoices',
    value: '10532',
    subtitle: '10532 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'Estimated volume is the total invoices by status in a filtered time period.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'past-invoices',
    title: 'Past Invoices',
    value: '10532',
    subtitle: '10532 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'Total number of invoices from previous billing periods.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'past-due-invoices',
    title: 'Past Due Invoices',
    value: '10532',
    subtitle: '10532 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'Number of invoices that are overdue and have not been paid by the due date.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'outstanding-invoices',
    title: 'Outstanding invoices',
    value: '10532',
    subtitle: '10532 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'Estimated volume is the total outstanding invoices by time period.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  // Quotes Category
  {
    id: 'quotes',
    title: 'Quotes',
    value: '10532',
    subtitle: '10532 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'Total number of quotes created for potential customers during the selected period.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'active-billing-schedules',
    title: 'Active Billing schedules',
    value: '10532',
    subtitle: '10532 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'Number of active billing schedules that are currently in effect.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'expiring-billing-schedules',
    title: 'Expiring Billing schedules',
    value: '10532',
    subtitle: '10532 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'Number of billing schedules that are set to expire soon or have recently expired.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  // Connect Category
  {
    id: 'connect-collected-fees',
    title: 'Connect collected fees',
    value: 'EUR €0.00',
    subtitle: 'EUR €0.00 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'Estimated revenue from application fees collected by this platform.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'new-connected-accounts',
    title: 'New connected accounts',
    value: '0',
    subtitle: '0 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'The number of newly connected accounts. Disconnected or deleted accounts are still counted in this chart.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
  {
    id: 'connect-gross-volume',
    title: 'Connect gross volume',
    value: 'EUR €0.00',
    subtitle: 'EUR €0.00 (previous period)',
    chartType: 'line',
    visible: false,
    tooltip: 'Estimated revenue from payments collected by this platform.',
    chartData: [
      { name: 'Jan', value: 0 },
      { name: 'Feb', value: 0 },
      { name: 'Mar', value: 0 },
      { name: 'Apr', value: 0 },
      { name: 'May', value: 0 },
      { name: 'Jun', value: 0 },
    ],
  },
]

// Dropdown Options
export const GROSS_VOLUME_OPTIONS = [
  { value: 'gross-volume', label: 'Gross volume' },
  { value: 'net-volume', label: 'Net volume' },
  { value: 'payments', label: 'Payments' },
  { value: 'refunds', label: 'Refunds' },
]

export const YESTERDAY_OPTIONS = [
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'today', label: 'Today' },
  { value: 'last-7-days', label: 'Last 7 days' },
  { value: 'last-30-days', label: 'Last 30 days' },
  { value: 'last-week', label: 'Last week' },
  { value: 'last-month', label: 'Last month' },
]

// Filter Options
export const DATE_FILTER_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last-7-days', label: 'Last 7 days' },
  { value: 'last-30-days', label: 'Last 30 days' },
  { value: 'last-week', label: 'Last week' },
  { value: 'last-month', label: 'Last month' },
]

export const INTERVAL_OPTIONS = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
]

export const COMPARE_OPTIONS = [
  { value: 'no-comparison', label: 'No comparison' },
  { value: 'previous-period', label: 'Previous period' },
  { value: 'previous-year', label: 'Previous year' },
]

// Add empty state sections to OVERVIEW_SECTIONS
export const EMPTY_STATE_SECTIONS: OverviewSection[] = [
  {
    id: 'net-volume-from-sales',
    title: 'Net volume from sales',
    value: 'EUR €0.00',
    subtitle: 'EUR €0.00 previous period',
    chartType: 'none',
    visible: false,
    chartData: [],
  },
  {
    id: 'top-customers-by-spend',
    title: 'Top customers by spend',
    value: '',
    subtitle: '',
    chartType: 'none',
    visible: false,
    chartData: [],
  },
]
